import { useCallback, useEffect, useRef, useState } from 'react';
import { ScanCommand } from '@aws-sdk/lib-dynamodb';
import { fetchAuthSession } from 'aws-amplify/auth';
import { initializeDynamoDBClient } from 'aws/dynamodb';

// Define the notification item structure based on the actual table
interface NotificationItem {
  examId: string;
  timestamp: number;
  title: string;
  message: string;
  senderName: string;
  senderId: string;
  ttl?: number;
  // Client-side only field for UI purposes
  status?: 'read' | 'unread';
}

interface UseNotificationsOptions {
  limit?: number;
  refreshInterval?: number;
}

export const useNotifications = ({
  limit = 20,
  refreshInterval = 10000, // 10 seconds
}: UseNotificationsOptions = {}) => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  
  // Track seen notifications in localStorage for persistence
  const seenNotificationsRef = useRef<Set<string>>(new Set());
  
  // Initialize seen notifications from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem('seen_notifications');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          seenNotificationsRef.current = new Set(parsed);
        }
      }
    } catch (e) {
      console.error('Failed to load seen notifications from localStorage', e);
    }
  }, []);
  
  // Save seen notifications to localStorage
  const updateSeenNotifications = useCallback((keys: string[]) => {
    keys.forEach(key => seenNotificationsRef.current.add(key));
    try {
      localStorage.setItem(
        'seen_notifications', 
        JSON.stringify(Array.from(seenNotificationsRef.current))
      );
    } catch (e) {
      console.error('Failed to save seen notifications to localStorage', e);
    }
  }, []);
  
  const intervalRef = useRef<NodeJS.Timeout>();
  const isMounted = useRef(true);
  
  const fetchNotifications = useCallback(async (silent = false) => {
    if (!isMounted.current) return;
    
    if (!silent) {
      setLoading(true);
    }
    
    try {
      // Check if the user is authenticated
      const session = await fetchAuthSession();
      if (!session.tokens) {
        console.error("Authentication required to fetch notifications");
        setError(new Error("Authentication required to fetch notifications"));
        setLoading(false);
        return;
      }
      
      // Initialize DynamoDB client with authenticated credentials
      const dynamoDB = await initializeDynamoDBClient();
      
      // Get table name from environment variable
      const tableName = process.env.REACT_APP_NOTIFICATION_TABLE;
      if (!tableName) {
        console.error("REACT_APP_NOTIFICATION_TABLE environment variable is not set");
        setError(new Error("Notification table name is not configured"));
        setLoading(false);
        return;
      }
      
      // Fetch notifications from DynamoDB
      const command = new ScanCommand({
        TableName: tableName,
        Limit: limit,
      });
      
      const response = await dynamoDB.send(command);
      
      if (response.Items && response.Items.length > 0) {
        // Map DynamoDB items to our notification format and sort by timestamp
        const notificationItems = response.Items
          .map(item => {
            const id = `${item.examId}-${item.timestamp}`;
            return {
              examId: item.examId,
              timestamp: item.timestamp,
              title: item.title,
              message: item.message,
              senderName: item.senderName,
              senderId: item.senderId,
              ttl: item.ttl,
              // Set status based on whether we've seen this notification before
              status: seenNotificationsRef.current.has(id) ? 'read' : 'unread',
            };
          })
          .sort((a, b) => b.timestamp - a.timestamp) // Sort by timestamp descending
          .slice(0, limit) as NotificationItem[]; // Take only the most recent 'limit' items
        
        setNotifications(notificationItems);
        setError(null);
      } else {
        // No items found
        setNotifications([]);
      }
    } catch (err) {
      console.error("Error fetching notifications:", err);
      setError(err instanceof Error ? err : new Error("Failed to fetch notifications"));
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [limit]);
  
  // Mark all as read
  const markAllAsRead = useCallback(() => {
    if (notifications.length === 0) return;
    
    const notificationIds = notifications.map(n => `${n.examId}-${n.timestamp}`);
    updateSeenNotifications(notificationIds);
    
    setNotifications(prev => 
      prev.map(notification => ({
        ...notification,
        status: 'read'
      }))
    );
  }, [notifications, updateSeenNotifications]);
  
  // Mark a single notification as read
  const markAsRead = useCallback((examId: string, timestamp: number) => {
    const id = `${examId}-${timestamp}`;
    updateSeenNotifications([id]);
    
    setNotifications(prev => 
      prev.map(notification => 
        notification.examId === examId && notification.timestamp === timestamp
          ? { ...notification, status: 'read' }
          : notification
      )
    );
  }, [updateSeenNotifications]);
  
  useEffect(() => {
    isMounted.current = true;
    
    // Initial fetch
    fetchNotifications();
    
    // Set up interval for periodic fetching
    intervalRef.current = setInterval(() => {
      fetchNotifications(true);
    }, refreshInterval);
    
    // Cleanup on unmount
    return () => {
      isMounted.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchNotifications, refreshInterval]);
  
  // Calculate unread count
  const unreadCount = notifications.filter(item => item.status === 'unread').length;
  
  return {
    data: notifications,
    loading,
    error,
    unread: unreadCount,
    refresh: () => fetchNotifications(),
    markAllAsRead,
    markAsRead,
  };
};
